/**
 * Колокольчик уведомлений в шапке сайдбара.
 *
 * Панель уходит в портал с `position: fixed`: сам сайдбар — `overflow-hidden`,
 * и абсолютно позиционированная панель внутри него обрезалась бы. Координаты
 * считаются от кнопки и зажимаются в окно, как у остальных поповеров проекта.
 */
import { Bell } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useNotificationActionsV2,
  useNotificationsV2,
} from '@/entities/notification';
import { getApiErrorMessage } from '@/shared/api';
import { useAuthStore } from '@/store/auth';

import { NotificationList } from './NotificationList';

const PANEL_WIDTH = 340;
const PANEL_MAX_HEIGHT = 460;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Уведомления — ручка нового бэка. Под старой сессией Bitrix24 её нет, и
  // опрашивать её раз в минуту незачем.
  const hasNativeSession = useAuthStore((state) => Boolean(state.nativeUser));

  // Лента тянется постоянно (раз в минуту) — счётчик на кнопке должен быть
  // актуален и с закрытой панелью.
  const { data } = useNotificationsV2(30, hasNativeSession);
  const { markAllRead } = useNotificationActionsV2();
  const count = data?.length ?? 0;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', close);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const toggle = () => {
    if (open) {
      close();

      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        left: Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 12),
        top: Math.min(
          rect.bottom + 8,
          window.innerHeight - PANEL_MAX_HEIGHT - 12
        ),
      });
    }
    setOpen(true);
  };

  if (!hasNativeSession) {
    return null;
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={count ? `Уведомления: ${count}` : 'Уведомления'}
        className="relative flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#f9f9fd] text-[#313846] shadow-[0_8px_22px_rgba(17,24,39,0.035)] transition-colors hover:bg-[#F1F3F8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#102653]/20"
      >
        <Bell className="size-5" strokeWidth={1.8} />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-[#F0443E] px-1 text-[11px] font-medium leading-[18px] text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[120] flex flex-col overflow-hidden rounded-[16px] border border-[#E7EAF0] bg-white shadow-[0_18px_45px_rgba(17,24,39,0.18)]"
            style={{
              left: Math.max(12, pos.left),
              top: Math.max(12, pos.top),
              width: PANEL_WIDTH,
              maxHeight: PANEL_MAX_HEIGHT,
            }}
          >
            <div className="flex items-center justify-between border-b border-[#EEF0F4] px-4 py-3">
              <span className="text-[14px] font-medium text-[#17244A]">
                Уведомления
              </span>
              <button
                type="button"
                disabled={count === 0 || markAllRead.isPending}
                onClick={() =>
                  markAllRead.mutate(undefined, {
                    onError: (error) =>
                      toast.error(
                        getApiErrorMessage(error, 'Не удалось отметить все')
                      ),
                  })
                }
                className="text-[13px] text-[#102653] transition-opacity hover:opacity-70 disabled:opacity-40"
              >
                Прочитать все
              </button>
            </div>

            <NotificationList
              className="min-h-0 flex-1 overflow-y-auto"
              onNavigate={close}
            />

            <button
              type="button"
              onClick={() => {
                close();
                navigate('/notifications');
              }}
              className="border-t border-[#EEF0F4] px-4 py-2.5 text-[13px] text-[#102653] transition-colors hover:bg-[#F6F7FA]"
            >
              Все уведомления и настройки
            </button>
          </div>,
          document.body
        )}
    </>
  );
}
